import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Youtube } from 'lucide-react';
import { Button } from './ui/button';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Courses', path: '/courses' },
    { name: 'Mentors', path: '/mentors' }
  ];

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-offwhite/95 backdrop-blur supports-[backdrop-filter]:bg-offwhite/80 border-b border-navy/10' : 'bg-offwhite border-b border-navy/10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3 group">
            <img
              src="/assets/logo.jpg"
              alt="Koley Chemistry Classes"
              style={{ width: '140px', height: '140px', objectFit: 'cover' }}
            />
            <div className="hidden sm:block">
              <span className="text-navy font-bold text-xl">Koley Chemistry</span>
              <p className="text-navy/60 text-xs">Classes</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-medium transition-all duration-300 relative group ${
                  location.pathname === link.path
                    ? 'text-electric'
                    : 'text-navy/80 hover:text-navy'
                }`}
              >
                {link.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-electric transition-all duration-300 group-hover:w-full ${
                    location.pathname === link.path ? 'w-full' : 'w-0'
                  }`}
                />
              </Link>
            ))}
            <a
              href="https://youtube.com/@koleychemistryclasseskcc9401"
              target="_blank"
              rel="noopener noreferrer"
              className="text-navy/70 hover:text-navy transition-colors duration-300"
            >
              <Youtube className="w-5 h-5" />
            </a>
            <a
              href="/assets/brochure.pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-electric text-electric hover:bg-electric/10 px-6 py-2 rounded-full">
                View Brochure
              </Button>
            </a>
            <a
              href="https://api.whatsapp.com/send?phone=7595888451"
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button className="bg-electric hover:bg-electric-dark text-offwhite px-6 py-2 rounded-full">
                Enroll Now
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-navy hover:text-electric transition-colors duration-300"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-300 overflow-hidden ${
          isMobileMenuOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-4 pt-2 pb-6 space-y-3 bg-offwhite">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              onClick={() => setIsMobileMenuOpen(false)}
              className={`block px-4 py-2 rounded-lg transition-all duration-300 ${
                location.pathname === link.path
                  ? 'bg-electric/10 text-electric'
                  : 'text-navy/80 hover:bg-navy/5 hover:text-navy'
              }`}
            >
              {link.name}
            </Link>
          ))}
          <a
            href="https://youtube.com/@koleychemistryclasseskcc9401"
            target="_blank"
            rel="noopener noreferrer"
            className="block px-4 py-2 rounded-lg text-navy/80 hover:bg-navy/5 hover:text-navy transition-all duration-300"
          >
            Youtube
          </a>
          <a
            href="/assets/brochure.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button variant="outline" className="w-full border-electric text-electric hover:bg-electric/10 py-3 rounded-lg">
              View Brochure
            </Button>
          </a>
          <a
            href="https://api.whatsapp.com/send?phone=7595888451"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button className="w-full bg-electric hover:bg-electric-dark text-offwhite py-3 rounded-lg">
              Enroll Now
            </Button>
          </a>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
